import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import {
	getSnippetMenuRank,
	TEMPLATE_GALLERY_MENU_RANK,
} from '@atlaskit/editor-common/quick-insert/snippet-menu-rank';
import { getSnippetPreviewImageUrls } from '@atlaskit/editor-common/quick-insert/snippet-preview-image-urls';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

import { ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import {
	buildInsertSnippetEventData,
	fireSnippetUIEvent,
} from '../hooks/analytics/snippetAnalyticsPayload.ts';
import type { Snippet } from '../types/snippet.ts';
import {
	extractSnippetKeywords,
	createSnippetDescription,
	filterAndSort,
	filterEligibleSnippets,
} from '../utils/quickInsertUtils.ts';

const messages = defineMessages({
	snippetsQuickInsertTitle: {
		id: 'snippets.snippetsQuickInsertTitle',
		defaultMessage: 'Block template gallery',
		description: 'Title for the block template quick insert item',
	},
	newLozenge: {
		id: 'snippets.quickInsert.newLozenge',
		defaultMessage: 'New',
		description: 'Text in lozenge next to the Block Template quick insert item',
	},
	newLozengeUpdated: {
		id: 'snippets.quickInsert.newLozenge.updated',
		defaultMessage: 'New',
		description: 'Sentence case text in lozenge next to the Block Template quick insert item',
	},
	snippetIconLabel: {
		id: 'snippets.snippetIconLabel',
		defaultMessage: 'Block template icon',
		description:
			'The text is used as the accessible label for the block template icon displayed in the quick-insert menu or extension manifest, describing the icon visually for assistive technologies.',
	},
	snippetQuickInsertDescription: {
		id: 'snippets.snippetQuickInsertDescription',
		defaultMessage: 'Create, discover, & insert block templates',
		description: 'Description for the block template quick insert item',
	},
});

type CreateSnippetExtensionProps = {
	snippets: Snippet[];
	intl: IntlShape;
	onOpenTemplatePanel?: () => void;
	userId?: string | null;
	createAnalyticsEvent?: CreateUIAnalyticsEvent | null;
};

/**
 * Fires an `insertSnippet` UI event with `origin=quickInsert` when a snippet is
 * inserted from the quick-insert menu via the extension-provider path.
 *
 * The extension `action` API does not expose analytics, so the
 * `createAnalyticsEvent` factory is captured from the build site (where the
 * editor's analytics context is available) and used here. No-op when the
 * factory is unavailable — keeping the event shape identical to the imperative
 * `createSnippetQuickInsertItem` path.
 */
const fireQuickInsertSnippetInsertedAnalytic = (
	createAnalyticsEvent: CreateUIAnalyticsEvent | null | undefined,
	snippet: Snippet,
) => {
	fireSnippetUIEvent(
		createAnalyticsEvent,
		buildInsertSnippetEventData(snippet),
		ANALYTICS_ORIGIN.QUICK_INSERT,
	);
};

export const snippetExtensionManifest = ({
	snippets,
	intl,
	onOpenTemplatePanel,
	userId,
	createAnalyticsEvent,
}: CreateSnippetExtensionProps): ExtensionManifest => {
	const snippetsQuickInsertTitle = intl.formatMessage(messages.snippetsQuickInsertTitle);
	const snippetIconLabel = intl.formatMessage(messages.snippetIconLabel);
	const snippetQuickInsertDescription = intl.formatMessage(messages.snippetQuickInsertDescription);
	const isSlashCommandEnabled = isExperimentEnabled('platform_editor_slash_command');
	const eligibleSnippets = isSlashCommandEnabled
		? filterEligibleSnippets(snippets, userId)
		: filterAndSort(snippets, userId);
	const quickInsert: ExtensionModule[] = eligibleSnippets.map((snippet) => {
		const previewImageUrls =
			snippet.scope === SnippetScope.GLOBAL && isSlashCommandEnabled
				? getSnippetPreviewImageUrls(snippet.snippetId)
				: undefined;
		return {
			key: `snippet-${snippet.snippetId}`,
			...(isSlashCommandEnabled
				? {
						category: 'block-templates',
						priority: getSnippetMenuRank({
							insertCount: snippet.insertCount,
							isFavorite: snippet.isFavorite,
							scope: snippet.scope,
							updatedAt: snippet.updatedAt.getTime(),
						}),
					}
				: {}),
			title: snippet.name,
			description: createSnippetDescription(snippet.createdBy, intl, snippet.description),
			keywords: extractSnippetKeywords(intl),
			icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
			preview: previewImageUrls
				? {
						image: previewImageUrls,
						attribution: {
							name: intl.formatMessage(quickInsertMessages.previewAttributionAtlassian),
						},
					}
				: undefined,
			action: async () => {
				// Only record an insert event when there is a body to insert, matching
				// the imperative `createSnippetQuickInsertItem` path which guards on a
				// successful insert before firing.
				if (snippet.body) {
					fireQuickInsertSnippetInsertedAnalytic(createAnalyticsEvent, snippet);
				}
				return snippet.body;
			},
		};
	});
	// Add "Block Template" quick insert item if callback is provided
	if (onOpenTemplatePanel) {
		quickInsert.unshift({
			key: 'block-template',
			...(isSlashCommandEnabled
				? { category: 'block-templates', priority: TEMPLATE_GALLERY_MENU_RANK }
				: {}),
			title: snippetsQuickInsertTitle,
			description: snippetQuickInsertDescription,
			keywords: extractSnippetKeywords(intl),
			icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
			lozenge: (
				<Lozenge appearance="discovery">{intl.formatMessage(messages.newLozengeUpdated)}</Lozenge>
			),
			action: async () => {
				onOpenTemplatePanel();
			},
		});
	}

	return {
		title: snippetsQuickInsertTitle,
		description: snippetQuickInsertDescription,
		type: 'com.atlassian.confluence.macro.core',
		key: 'snippet-extension',
		icons: {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () => import('@atlaskit/icon-lab/core/template'),
		},
		modules: {
			quickInsert,
		},
	};
};
